import { Navigate, useNavigate } from 'react-router-dom'
import { useEffect, useMemo } from 'react'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import { APP_ROUTES } from '../constants'
import { resolveNormalizedHomeRoute } from '../navigation/accountRoutes'
import { useAppContainerLogic } from './layout/useAppContainerLogic'
import { AppShell } from './layout/AppShell'
import { getAppContainerRedirect } from './layout/getAppContainerRedirect'

/** Production page — wires useAppContainerLogic result to AppShell.
 *  Navigation guards are kept here (not in the hook) since they are route concerns, not business logic.
 */
export const AppContainer = () => {
    const navigate = useNavigate()
    const logic = useAppContainerLogic()

    // Navigation Guard: account confirmation, then onboarding - memoized for performance.
    // Priority order (and why) is documented in getAppContainerRedirect.
    const redirect = useMemo(
        () =>
            getAppContainerRedirect({
                user: logic.user,
                account: logic.account,
                accounts: logic.accounts,
                primaryAccount: logic.primaryAccount,
                isAccountSwitching: logic.isAccountSwitching,
                isAccountsLoaded: logic.isAccountsLoaded,
                pathname: logic.location.pathname,
            }),
        [
            logic.user,
            logic.account,
            logic.accounts,
            logic.primaryAccount,
            logic.isAccountSwitching,
            logic.isAccountsLoaded,
            logic.location.pathname,
        ],
    )

    useEffect(() => {
        if (redirect) {
            navigate(redirect)
        }
    }, [redirect, navigate])

    if (!logic.isAccountsLoaded) return <FullScreenLoader dark />

    // Navigation Guard: Normalized route redirect
    const normalizedRoute =
        !logic.isAccountSwitching &&
        resolveNormalizedHomeRoute({
            pathname: logic.location.pathname,
            primaryAccount: logic.primaryAccount,
            activeAccountId: logic.activeAccountId,
        })

    if (normalizedRoute) {
        return <Navigate to={normalizedRoute} replace />
    }

    // Navigation Guard: don't mount AppShell (and its account-scoped queries, e.g. holdings)
    // while a redirect to account confirmation/onboarding is pending - otherwise it renders
    // for one frame before the effect above navigates away.
    if (redirect) {
        return <FullScreenLoader dark />
    }

    // Navigation Guard: Defer dashboard render while switching
    // Defer dashboard render only if we're waiting for account switch to complete
    const shouldDeferDashboardRender =
        logic.location.pathname === APP_ROUTES.DASHBOARD && !logic.activeAccountId && logic.isAccountSwitching

    if (shouldDeferDashboardRender) {
        return <FullScreenLoader dark />
    }

    return (
        <AppShell
            user={logic.user}
            activeAccountId={logic.activeAccountId}
            rewardsCount={logic.rewardsCount}
            showAdviserChrome={logic.showAdviserChrome}
            appShellVisibility={logic.appShellVisibility}
            canGoBack={logic.canGoBack}
            isAdviserMenuOpen={logic.isAdviserMenuOpen}
            onBackClick={logic.goBackSafely}
            onAdviserMenuToggle={logic.setIsAdviserMenuOpen}
        />
    )
}
